import React, { useId, useState } from 'react'
import { Link } from 'react-router'
import { Copy } from 'lucide-react'
import { Badge } from '../ui'
import { useCopyWithToast } from '../profile/ProfileActionLinks'
import { StrengthBars } from '../profile/network/NetworkList'
import type { ConnectionNode } from '../../services/apiClient'
import {
  TAG_LABEL,
  headline,
  sharedRoles,
  summaryText,
  viaText,
  type ConnectionSummary,
} from './connectionSummary'

const SHOWN = 6

function href(id: string): string | null {
  if (id.startsWith('cvr:')) return `/company/${id.slice(4)}`
  if (id.startsWith('person:')) return `/person/${id.slice(7)}`
  return null
}

/**
 * The answer first: what the two are to each other, in one line, then
 * every company (or person) they share, strongest tie on top.
 */
const ConnectionOverview: React.FC<{
  summary: ConnectionSummary
  from: ConnectionNode
  to: ConnectionNode
  eyebrow?: string
}> = ({ summary, from, to, eyebrow = 'Direkte forbindelse' }) => {
  const copyText = useCopyWithToast()
  const headingId = useId()
  const [showAll, setShowAll] = useState(false)
  const head = headline(summary, from, to)
  if (!head) return null
  const { shared } = summary
  const visible = showAll ? shared : shared.slice(0, SHOWN)
  const people = shared.length > 0 && shared.every((s) => s.middle.kind === 'person')
  const graphTarget = shared.find((s) => s.middle.id.startsWith('cvr:'))?.middle

  return (
    <section
      aria-labelledby={headingId}
      className="rounded-[10px] border border-rule bg-paper px-5 py-5"
      data-testid="connection-overview"
    >
      <p className="text-[13px] font-semibold text-ink-500">{eyebrow}</p>
      <h2
        id={headingId}
        className="mt-1 text-[21px] font-bold leading-tight text-ink"
      >
        {head.title}
      </h2>
      <p className="mt-1 text-[15px] text-ink-700">{head.detail}</p>

      {shared.length > 0 && (
        <>
          <h3 className="mt-5 text-[13px] font-semibold text-ink-500">
            {people ? 'Fælles personer' : 'Fælles selskaber'}{' '}
            <span className="tabular-nums">· {shared.length}</span>
          </h3>
          <ul className="mt-1.5 divide-y divide-rule-soft border-t border-rule-soft">
            {visible.map((s) => {
              const link = href(s.middle.id)
              return (
                <li key={s.middle.id} className="flex items-start gap-3 py-2.5">
                  <StrengthBars level={s.level} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      {link ? (
                        <Link
                          to={link}
                          className="font-semibold text-ink hover:underline decoration-ink-300 underline-offset-[3px]"
                        >
                          {s.middle.name}
                        </Link>
                      ) : (
                        <span className="font-semibold text-ink">{s.middle.name}</span>
                      )}
                      {s.tags.map((t) => (
                        <Badge key={t} tone={t === 'partnership' ? 'active' : 'neutral'}>
                          {TAG_LABEL[t]}
                        </Badge>
                      ))}
                    </div>
                    <p className="mt-0.5 text-[13px] leading-snug text-ink-500">
                      {sharedRoles(s)}
                    </p>
                    {[...s.fromVia.map((v) => viaText(s.path.nodes[0], v)), ...s.toVia.map((v) => viaText(s.path.nodes[2], v))].map((t) => (
                      <p key={t} className="mt-0.5 text-[13px] leading-snug text-ink-500">
                        {t}
                      </p>
                    ))}
                  </div>
                </li>
              )
            })}
          </ul>
          {shared.length > visible.length && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="mt-1 text-[13px] font-semibold text-ink-500 hover:text-ink"
            >
              Vis {shared.length - visible.length} mere
            </button>
          )}
        </>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void copyText(summaryText(summary, from, to), 'Overblikket er kopieret.')}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-rule-strong bg-paper px-3 text-[13px] font-semibold text-ink hover:border-ink-300"
        >
          <Copy className="h-3.5 w-3.5" aria-hidden />
          Kopiér overblik
        </button>
        {graphTarget && (
          <Link
            to={`/company/${graphTarget.id.slice(4)}`}
            className="inline-flex h-8 items-center rounded-md border border-rule-strong bg-paper px-3 text-[13px] font-semibold text-ink hover:border-ink-300"
          >
            Vis {graphTarget.name} i grafen
          </Link>
        )}
      </div>
    </section>
  )
}

export default ConnectionOverview
